import React from "react";

import Header from "../Header";
import { Loader } from "../Loader";
import { WidgetError } from "../WidgetError";

import { Axis } from "./Axis";
import { Bars } from "./Bars";
import { ChartContainer, ChartDataPoint, ChartState } from "./ChartContext";
import style from "./StandardBarChart.scss";

export interface StandardBarChartProps {
  /** Data points to be displayed in the chart */
  data: ChartDataPoint[];
  /** Description of the chart */
  chartDescription?: string;
  /** Label for the Y-axis */
  yAxisLabel?: string;
  /** Function to render custom tooltip content. If you don't want to render a tooltip, use `null` */
  tooltipContent?: null | ((args: { data: ChartDataPoint }) => JSX.Element);
  /** Flag to indicate if there is an error */
  error?: boolean;
  /** Flag to indicate if the chart is loading */
  loading?: boolean;
}

interface Dimensions {
  /** Width of the chart container */
  width: number | null;
  /** Height of the chart container */
  height: number | null;
}

export function StandardBarChart({
  data,
  chartDescription = "",
  yAxisLabel = "",
  tooltipContent,
  error = false,
  loading = false,
}: StandardBarChartProps) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const dimensions = useResizeObserver(containerRef);
  const ctxValue: ChartState = React.useMemo(
    () => ({
      data,
      chartDescription,
      yAxisLabel,
      tooltipContent,
      margin: { top: 8, right: 10, bottom: 20, left: 40 },
      dimensions,
      svgRef: { current: null },
    }),
    []
  );

  if (loading) {
    return (
      <div data-testid="chart-loader">
        <Loader centered />
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{ display: "flex", justifyContent: "center" }}
        data-testid="chart-error"
      >
        <WidgetError text="There was an issue loading this chart" />
      </div>
    );
  }

  return (
    <div className={style["standard-chart-bar"]} ref={containerRef}>
      <ChartContainer state={ctxValue}>
        <Axis />
        <Bars />
      </ChartContainer>
      {chartDescription ? (
        <div
          className={style["chart-description"]}
          data-testid="chart-description"
        >
          <Header size="h6" level="6">
            {chartDescription}
          </Header>
        </div>
      ) : null}
    </div>
  );
}

export function useResizeObserver(
  ref: React.RefObject<HTMLDivElement>
): Dimensions {
  const [dimensions, setDimensions] = React.useState<Dimensions>({
    width: null,
    height: null,
  });
  React.useEffect(() => {
    const observeTarget = ref.current;
    if (!observeTarget) {
      return;
    }
    const resizeObserver = new ResizeObserver(
      // istanbul ignore next - not worth mocking the observer and testing it
      (entries) => {
        entries.forEach((entry) => {
          setDimensions({
            width: entry.contentRect.width,
            height: entry.contentRect.height,
          });
        });
      }
    );
    resizeObserver.observe(observeTarget);
    return () => {
      resizeObserver.unobserve(observeTarget);
    };
  }, [ref]);
  return dimensions;
}
